import { Button, Form, Input, message, Space } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import httpClient from "../../../api/httpClient";
import { useDispatch } from "react-redux";
import { inviteUserInTenant } from "../../../store/users/thunks";
import Modal from "../../../components/generic/Modal";
import Select from "../../../components/generic/Select";
import PhoneInput from "../../../components/shared/PhoneInput";
import { parseError } from "../../../utils/shared";

const AddEditUsers = (props) => {
  const {
    selectedUser,
    handleCancel,
    users,
    selectedTenantId: tenant_id,
  } = props;

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [emailCheck, setEmailCheck] = useState({
    checked: false,
    loading: false,
    userData: null,
  });

  const [submitting, setSubmitting] = useState(false);

  const isEdit = useMemo(() => !!selectedUser?.id, [selectedUser]);

  const userExists = useMemo(
    () => !!emailCheck?.userData,
    [emailCheck?.userData],
  );

  // Initialize form with editing user data
  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue(selectedUser);
      setEmailCheck({
        checked: true,
        loading: false,
        userData: selectedUser,
      });
    }
  }, [selectedUser, form, isEdit]);

  // API call to check if user exists by email
  const checkEmailExists = useCallback(async () => {
    try {
      await form.validateFields(["email"]);
      const email = form.getFieldValue("email");

      setEmailCheck((prev) => ({ ...prev, loading: true }));

      const { data } = await httpClient.post(`invite-user`, {
        tenant_id,
        email,
        type: "CHECK_USER_EXIST",
      });

      if (data?.id) {
        form.setFieldsValue({
          first_name: data?.first_name,
          last_name: data?.last_name,
          mobile_number: data?.mobile_number,
        });
        message.info("User already exists. Please select a role.");

        setEmailCheck({ checked: true, loading: false, userData: data });
      } else {
        message.success("New user. Please fill in the details.");
        setEmailCheck({ checked: true, loading: false, userData: null });
      }
    } catch (error) {
      console.log("🚀 ~ AddEditModal.jsx:71 ~ AddEditUsers ~ error:", error);
      setEmailCheck((prev) => ({ ...prev, loading: false }));
      parseError(error, true);
    }
  }, [form, tenant_id]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        await dispatch(
          inviteUserInTenant({
            ...values,
            tenant_id,
            type: isEdit
              ? "CHANGE_ROLE"
              : emailCheck.userData
                ? "INVITE"
                : "CREATE",
          }),
        );

        handleCancel();
      } catch (error) {
        console.log("🚀 ~ AddEditModal.jsx:94 ~ AddEditUsers ~ error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      isEdit,
      selectedUser,
      users,
      userExists,
      emailCheck.userData,
      handleCancel,
    ],
  );

  return (
    <Modal
      isProcessing={submitting}
      title={isEdit ? "Edit User" : "Add New User"}
      onCancel={handleCancel}
      footer={null}
      width={500}
      open
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={submitting}
      >
        {/* Step 1: Email Input - Always visible */}
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter valid email" },
          ]}
        >
          <Input
            placeholder="info@company.com"
            disabled={isEdit || emailCheck.checked}
          />
        </Form.Item>

        {/* Step 2: Check Email Button - Show only for new users before verification */}
        {!isEdit && !emailCheck.checked && (
          <Form.Item>
            <Button
              type="primary"
              onClick={checkEmailExists}
              loading={emailCheck.loading}
              block
            >
              {emailCheck.loading ? "Checking..." : "Check Email"}
            </Button>
          </Form.Item>
        )}

        {/* Step 3: Show form fields after email verification */}
        {emailCheck.checked && (
          <>
            {/* Role Selection - Always required after email check */}
            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: "Please select a role" }]}
            >
              <Select
                placeholder="Select Role"
                onChange={(value) => {
                  // Clear custom role if user selects predefined role
                  if (value !== "other") {
                    form.setFieldValue("custom_role", undefined);
                  }
                }}
                options={[
                  { value: "ADMIN", label: "Admin" },
                  { value: "ADVISOR", label: "Advisor" },
                  { value: "USER", label: "User" },
                  { value: "OTHER", label: "Other" },
                ]}
              />
            </Form.Item>

            {/* Custom Role Input - Show only when "Other" is selected */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.role !== currentValues.role
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("role") === "other" ? (
                  <Form.Item
                    label="Custom Role"
                    name="custom_role"
                    rules={[
                      { required: true, message: "Please specify the role" },
                    ]}
                  >
                    <Input placeholder="Enter Custom Role" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            {/* Existing User Info Display - Read-only info box */}
            {userExists && (
              <div>
                <p>Existing User Details:</p>
                <p style={{ margin: "5px 0" }}>
                  <strong>Name:</strong> {emailCheck?.userData?.first_name}{" "}
                  {emailCheck?.userData?.last_name}
                </p>
                <p style={{ margin: "5px 0" }}>
                  <strong>Phone:</strong> {emailCheck?.userData?.mobile_number}
                </p>
              </div>
            )}

            {!userExists && (
              <>
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[
                    { required: true, message: "Please enter the First Name" },
                  ]}
                >
                  <Input placeholder="Enter First Name" />
                </Form.Item>

                <Form.Item
                  label="Last Name"
                  name="last_name"
                  rules={[
                    { required: true, message: "Please enter the Last Name" },
                  ]}
                >
                  <Input placeholder="Enter Last Name" />
                </Form.Item>

                <PhoneInput />
              </>
            )}
            <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {isEdit ? "Update" : "Add"} User
                </Button>
              </Space>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddEditUsers;
